/**
 * DependencyChip - A compact inline pill for displaying dependencies in form rows.
 *
 * Shows a dependency with team-colored left border, truncated name, and optional
 * remove button. Can be made clickable for navigation.
 */

import type { HTMLAttributes } from 'react';
import { X } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';
import { SourceIdBadge } from './SourceIdBadge';

export interface DependencyChipProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /**
   * UUID of the dependency (for data attributes and callbacks).
   */
  dependencyId: string;
  /**
   * Display name of the dependency (will be truncated if too long).
   */
  name: string;
  /**
   * Team color for the left border indicator.
   */
  teamColor?: string;
  /**
   * Tooltip text shown on hover (e.g., "Feature | Team | Deliverable").
   */
  tooltip?: string;
  /**
   * Maximum width for the name before truncation.
   * @default '120px'
   */
  maxWidth?: string;
  /**
   * Callback when the chip is clicked (for navigation).
   * If not provided, the chip is not clickable.
   */
  onClick?: (dependencyId: string) => void;
  /**
   * Callback when the remove button is clicked.
   * If not provided, no remove button is shown.
   */
  onRemove?: (dependencyId: string) => void;
  /**
   * Whether to disable interactions (click and remove).
   */
  disabled?: boolean;
  /**
   * External tracker key (e.g. "PROJ-123"). When set, a compact source-id badge
   * is shown after the name so two same-named slices of one multi-team epic stay
   * distinguishable at a glance.
   */
  sourceId?: string | null;
}

/**
 * DependencyChip - Compact inline dependency pill for form rows.
 *
 * @example
 * ```tsx
 * // Basic chip with remove
 * <DependencyChip
 *   dependencyId="uuid-123"
 *   name="API Integration"
 *   teamColor="#3b82f6"
 *   onRemove={(id) => removeDependency(id)}
 * />
 *
 * // Clickable chip for navigation
 * <DependencyChip
 *   dependencyId="uuid-123"
 *   name="API Integration"
 *   teamColor="#3b82f6"
 *   tooltip="Backend | API Integration"
 *   onClick={(id) => navigateToDeliverable(id)}
 *   onRemove={(id) => removeDependency(id)}
 * />
 * ```
 */
export function DependencyChip({
  dependencyId,
  name,
  teamColor = '#64748b',
  tooltip,
  maxWidth = '120px',
  onClick,
  onRemove,
  disabled,
  sourceId,
  className,
  ...props
}: DependencyChipProps) {
  const isClickable = !!onClick && !disabled;

  const handleClick = () => {
    if (isClickable) {
      onClick(dependencyId);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onClick
    if (onRemove && !disabled) {
      onRemove(dependencyId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(dependencyId);
    }
  };

  return (
    <div
      className={cn(
        `inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs transition-all`,
        isClickable && 'cursor-pointer',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
      style={{
        backgroundColor: `${teamColor}25`, // 25 = ~15% opacity
        color: teamColor,
        ...(isClickable && !disabled ? { ['--hover-bg' as string]: `${teamColor}40` } : {}),
      }}
      onMouseEnter={(e) => {
        if (isClickable && !disabled) {
          e.currentTarget.style.backgroundColor = `${teamColor}40`; // ~25% opacity on hover
        }
      }}
      onMouseLeave={(e) => {
        if (isClickable && !disabled) {
          e.currentTarget.style.backgroundColor = `${teamColor}25`;
        }
      }}
      title={tooltip}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={isClickable ? 0 : undefined}
      role={isClickable ? 'button' : undefined}
      data-dependency-id={dependencyId}
      {...props}
    >
      <span className="truncate font-semibold" style={{ maxWidth }}>
        {name}
      </span>
      {sourceId && <SourceIdBadge sourceId={sourceId} variant="compact" size="sm" className="shrink-0" />}
      {onRemove && !disabled && (
        <button
          type="button"
          onClick={handleRemove}
          className="
            opacity-60 transition-opacity
            hover:opacity-100
            focus:outline-hidden
          "
          style={{ color: teamColor }}
          aria-label={`Remove ${name}`}
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
}
